/**
 * Healing system: priests restore hp to nearby wounded friendly units over time.
 * Picks the most-wounded ally in range (lowest hp fraction, ties broken by
 * nearest) and tops it up by rate*dt. A priest may heal other priests but never
 * itself, never enemies, never buildings. Deterministic (no RNG / wall clock).
 */
import type { World } from "../world";
import type { Registry } from "../../data/registry";
import type { Eid } from "../types";
import { FIXED_DT } from "../types";

/** Reused candidate buffer (single-threaded). */
const NEAR: Eid[] = [];

export function healingSystem(world: World, _reg: Registry): void {
  world.rebuildSpatial(); // index current positions for in-range target search
  for (const [eid, healer] of world.healer) {
    healer.active = false;

    const tr = world.transform.get(eid);
    if (!tr || !world.health.has(eid)) continue;
    const owner = world.owner.get(eid)?.player;
    if (owner === undefined) continue;

    const rangeSq = healer.range * healer.range;

    // Find the most-wounded friendly unit in range (lowest hp fraction; ties
    // broken by nearest, then lower eid). Candidates come from the spatial index;
    // the exact range/fraction/eid tests below keep the pick order-independent.
    let best: Eid | null = null;
    let bestFrac = Infinity;
    let bestD = Infinity;
    NEAR.length = 0;
    world.spatial.queryInto(tr.pos.x, tr.pos.y, healer.range, NEAR);
    for (const t of NEAR) {
      if (t === eid) continue;
      const tHealth = world.health.get(t);
      if (!tHealth) continue;
      if (world.owner.get(t)?.player !== owner) continue;
      if (world.ident.get(t)?.kind !== "unit") continue;
      if (tHealth.hp >= tHealth.maxHp) continue;
      const tTr = world.transform.get(t);
      if (!tTr) continue;
      const d = (tTr.pos.x - tr.pos.x) ** 2 + (tTr.pos.y - tr.pos.y) ** 2;
      if (d > rangeSq) continue;
      const frac = tHealth.hp / tHealth.maxHp;
      // most-wounded, ties by nearest, then by LOWER eid so the pick is
      // independent of Map iteration order (determinism under restore/MP).
      if (
        frac < bestFrac ||
        (frac === bestFrac && d < bestD) ||
        (frac === bestFrac && d === bestD && t < (best ?? Infinity))
      ) {
        bestFrac = frac;
        bestD = d;
        best = t;
      }
    }

    if (best === null) continue;
    const target = world.health.get(best)!;
    target.hp = Math.min(target.maxHp, target.hp + healer.rate * FIXED_DT);
    healer.active = true;
  }
}
